/**
 * Patient-viewer language preference (tab-scoped).
 * Keeps Hindi from leaking into staff sessions via i18next localStorage/cookie caches.
 */

import i18n from '@ohif/i18n';

const PATIENT_VIEW_LANG_KEY = 'patient_view_language';
const STAFF_LANG_BACKUP_KEY = 'staff_i18nextLng_backup';
const I18NEXT_LNG_KEY = 'i18nextLng';
const STAFF_DEFAULT_LANG = 'en-US';

function readStoredLang(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredLang(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function removeStoredLang(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getPatientViewLanguage(): string {
  const stored = readStoredLang(sessionStorage, PATIENT_VIEW_LANG_KEY);
  if (stored && (stored === 'hi' || stored.toLowerCase().startsWith('en'))) {
    return stored === 'hi' ? 'hi' : STAFF_DEFAULT_LANG;
  }
  return STAFF_DEFAULT_LANG;
}

export function setPatientViewLanguage(lng: string): void {
  const normalized = String(lng || '')
    .toLowerCase()
    .startsWith('hi')
    ? 'hi'
    : STAFF_DEFAULT_LANG;

  // Backup staff locale once so patient Hindi does not permanently overwrite it
  const current = String(i18n.language || readStoredLang(localStorage, I18NEXT_LNG_KEY) || '');
  if (current && !current.toLowerCase().startsWith('hi')) {
    if (!readStoredLang(sessionStorage, STAFF_LANG_BACKUP_KEY)) {
      writeStoredLang(sessionStorage, STAFF_LANG_BACKUP_KEY, current);
    }
  }

  writeStoredLang(sessionStorage, PATIENT_VIEW_LANG_KEY, normalized);
  i18n
    .changeLanguage(normalized)
    .then(() => {
      // Keep detector cache on session preference for patient tab only; staff restore clears this
      if (normalized === 'hi') {
        writeStoredLang(sessionStorage, PATIENT_VIEW_LANG_KEY, 'hi');
      }
    })
    .catch(() => {
      /* ignore */
    });
}

export function clearPatientViewLanguage(): void {
  removeStoredLang(sessionStorage, PATIENT_VIEW_LANG_KEY);
}

/** Reset i18n to prior staff language (or en-US) after patient session ends. */
export function resetI18nToStaffDefault(): void {
  clearPatientViewLanguage();
  const staff = readStoredLang(sessionStorage, STAFF_LANG_BACKUP_KEY) || STAFF_DEFAULT_LANG;
  removeStoredLang(sessionStorage, STAFF_LANG_BACKUP_KEY);
  writeStoredLang(localStorage, I18NEXT_LNG_KEY, staff);
  try {
    // Clear cookie cache used by i18next-browser-languagedetector
    document.cookie = 'i18next=; Max-Age=0; path=/';
  } catch {
    /* ignore */
  }
  const current = String(i18n.language || '').toLowerCase();
  if (current !== staff.toLowerCase()) {
    i18n.changeLanguage(staff).catch(() => {
      /* ignore */
    });
  }
}

export { STAFF_DEFAULT_LANG, PATIENT_VIEW_LANG_KEY };
